import type { Job } from "@/api/raptor";
import { useApi } from "@mb-pro-ui/utils";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Field, Form } from "react-final-form";

type PendingUpdate = {
  name: string;
  value: unknown;
  timestamp: number;
  retryCount: number;
};

type BaseField = {
  name: string;
  label: string;
  required?: boolean;
};

type CheckboxField = BaseField & {
  type: "checkbox";
};

type TextFieldField = BaseField & {
  type: "text";
};

type TextareaField = BaseField & {
  type: "textarea";
};

type SelectField = BaseField & {
  type: "select";
  options: string[];
};

export type FieldFormField =
  | CheckboxField
  | TextFieldField
  | TextareaField
  | SelectField;

export type SectionFormField = {
  title?: string;
  fields: FieldFormField[];
};

export type FormTemplate = SectionFormField[];

const ResponseJobReportForm = ({
  formValues,
  template,
  jobId,
}: {
  formValues?: Job["values"];
  template: FormTemplate;
  jobId: string;
}) => {
  const api = useApi();
  const safeFormValues = formValues ?? {};
  const safeTemplate = template ?? [];

  const lastSavedValuesRef = useRef<Record<string, unknown>>({
    ...safeFormValues,
  });
  const [pendingUpdates, setPendingUpdates] = useState<PendingUpdate[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const qc = useQueryClient();

  const mutation = useMutation<void, Error, Record<string, unknown>>({
    mutationFn: async (values: Record<string, unknown>) => {
      if (!isOnline) {
        throw new Error("Offline");
      }

      await api(`/actions/raptor/jobs/${jobId}/set-values`, {
        method: "POST",
        headers: {
          Accept: "application/vnd.api+json, application/json",
          "Content-Type": "application/vnd.api+json",
        },
        body: JSON.stringify(values),
      });
    },
    retry: (failureCount, error) => {
      if (error?.message === "Offline") return false;
      return failureCount < 5;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onSuccess: (_, variables) => {
      const fieldName = Object.keys(variables)[0];
      setPendingUpdates((prev) =>
        prev.filter((update) => update.name !== fieldName),
      );
      qc.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] === "raptor/jobs") return true;
          return false;
        },
      });
    },
    onError: (error, variables) => {
      const fieldName = Object.keys(variables)[0];
      const value = variables[fieldName];

      if (!isOnline || error?.message === "Offline") {
        setPendingUpdates((prev) => {
          const existing = prev.find((update) => update.name === fieldName);
          if (existing) {
            return prev.map((update) =>
              update.name === fieldName
                ? { ...update, value, timestamp: Date.now() }
                : update,
            );
          }
          return [
            ...prev,
            {
              name: fieldName,
              value,
              timestamp: Date.now(),
              retryCount: 0,
            },
          ];
        });
      }
    },
  });

  useEffect(() => {
    if (isOnline && pendingUpdates.length > 0) {
      const retryPendingUpdates = async () => {
        for (const update of pendingUpdates) {
          try {
            await mutation.mutateAsync({ [update.name]: update.value });
          } catch {
            setPendingUpdates((prev) =>
              prev.map((u) =>
                u.name === update.name
                  ? { ...u, retryCount: u.retryCount + 1 }
                  : u,
              ),
            );
          }
        }
      };

      const timeoutId = setTimeout(retryPendingUpdates, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [isOnline, pendingUpdates, mutation]);

  const handleFieldChange = (name: string, value: unknown) => {
    if (lastSavedValuesRef.current[name] === value) return;

    lastSavedValuesRef.current[name] = value;

    if (isOnline) {
      mutation.mutate({ [name]: value });
    } else {
      setPendingUpdates((prev) => {
        const existing = prev.find((update) => update.name === name);
        if (existing) {
          return prev.map((update) =>
            update.name === name
              ? { ...update, value, timestamp: Date.now() }
              : update,
          );
        }
        return [
          ...prev,
          {
            name,
            value,
            timestamp: Date.now(),
            retryCount: 0,
          },
        ];
      });
    }
  };

  const renderSection = (section: SectionFormField, index: number) => {
    const content = section.fields.map((field, idx) => (
      <Box key={idx} sx={{ mb: idx === section.fields.length - 1 ? 0 : 2 }}>
        {renderField(field)}
      </Box>
    ));

    if (section.title && section.title.trim() !== "") {
      return (
        <Paper key={index} sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: 1 }}>
          <Typography sx={{ fontSize: "1rem", mb: 1 }} fontWeight={500}>
            {section.title}
          </Typography>
          {content}
        </Paper>
      );
    }

    return (
      <Box key={index} sx={{ mb: 2 }}>
        {content}
      </Box>
    );
  };

  const renderField = (field: FieldFormField) => {
    switch (field.type) {
      case "checkbox":
        return (
          <Field
            name={field.name}
            type="checkbox"
            render={({ input }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...input}
                    size="medium"
                    onChange={(e) => {
                      input.onChange(e.target.checked);
                      handleFieldChange(field.name, e.target.checked);
                    }}
                  />
                }
                label={field.label}
                sx={{
                  "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
                }}
              />
            )}
          />
        );

      case "select":
        return (
          <Field
            name={field.name}
            render={({ input }) => (
              <TextField
                {...input}
                size="small"
                select
                required={field.required}
                fullWidth
                label={field.label}
                onBlur={() => handleFieldChange(field.name, input.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiSelect-select": {
                    fontSize: "0.9rem",
                  },
                }}
              >
                {field.options.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: "0.9rem" }}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        );

      case "textarea":
        return (
          <Field
            name={field.name}
            render={({ input }) => (
              <TextField
                {...input}
                label={field.label}
                fullWidth
                multiline
                required={field.required}
                rows={3}
                onBlur={() => handleFieldChange(field.name, input.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.9rem",
                  },
                }}
              />
            )}
          />
        );

      case "text":
        return (
          <Field
            name={field.name}
            render={({ input }) => (
              <TextField
                {...input}
                label={field.label}
                fullWidth
                size="small"
                required={field.required}
                onBlur={() => handleFieldChange(field.name, input.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.9rem",
                  },
                }}
              />
            )}
          />
        );
    }
  };

  return (
    <Form
      onSubmit={() => Promise.resolve()}
      initialValues={formValues}
      render={() => (
        <form>
          {safeTemplate.length > 0 &&
            safeTemplate.map((section, idx) => renderSection(section, idx))}
        </form>
      )}
    />
  );
};

export default ResponseJobReportForm;
